import * as React from 'react'
import { BirthInputResult, normalizeBirthTimeDraft, parseBirthTime } from '../../helpers/birthDateTime'
import { cn } from '../../helpers/utils'
import { useBirthInputMessages } from './birth-input-messages'
import { Input } from './input'

export type TimeInputProps = {
  id: string
  value: string
  onValueChange: (draft: string, result: BirthInputResult) => void
  language?: string
  required?: boolean
  disabled?: boolean
  label?: string
  help?: string
  error?: string
  /** Set on a submission attempt when the page uses custom form validation. */
  showErrors?: boolean
  placeholder?: string
  'aria-label'?: string
  className?: string
}

// Always a HH:MM text field instead of a native time input: the native one follows
// the browser/OS locale and may display AM/PM, while the app expects a 24-hour clock
// everywhere. Parsing lives in helpers/birthDateTime so that the same rules and cases
// are shared with the other birth inputs.

export function TimeInput({ id, value, onValueChange, language, required = false,
  disabled, label, help, error, showErrors = false, placeholder, className,
  'aria-label': ariaLabel,
}: TimeInputProps) {
  const { text, errorText } = useBirthInputMessages('time', language)
  const [validated, setValidated] = React.useState(false)
  const input = React.useRef<HTMLInputElement | null>(null)
  const result = parseBirthTime(value, required)
  const validationError = error || errorText(result.error)
  const visibleError = (showErrors || validated) ? validationError : undefined
  const description = `${id}-help${visibleError ? ` ${id}-error` : ''}`

  React.useEffect(() => {
    input.current?.setCustomValidity(validationError ?? '')
  }, [validationError])

  return (
    <div className={cn('min-w-0 space-y-2', className)}>
      <label htmlFor={id} className="block text-sm font-medium">{label ?? text('time')}</label>
      <p id={`${id}-help`} className="text-sm text-muted-foreground">{help ?? text('timeHelp')}</p>
      <Input id={id} ref={input} type="text" inputMode="numeric" autoComplete="off"
        required={required} disabled={disabled} spellCheck={false} value={value}
        placeholder={placeholder ?? 'HH:MM'}
        aria-label={ariaLabel} aria-describedby={description}
        aria-invalid={visibleError ? true : undefined}
        className="w-28 min-h-[44px] text-base md:text-base tabular-nums"
        onInvalid={() => setValidated(true)}
        // The draft is kept exactly as typed, pasted or edited in the middle: no
        // masking and no caret moves, and nothing is clamped, so an impossible
        // time is reported instead of corrected.
        onChange={event => {
          setValidated(false)
          const next = event.target.value
          onValueChange(next, parseBirthTime(next, required))
        }}
        onBlur={() => {
          setValidated(true)
          const next = normalizeBirthTimeDraft(value)
          if (next !== value) onValueChange(next, parseBirthTime(next, required))
        }} />
      {visibleError && <p id={`${id}-error`} role="alert" className="text-sm text-destructive">{visibleError}</p>}
    </div>
  )
}
